import { isSSR } from '@helpers/ssr';
import { useAnimation } from 'framer-motion';
import * as React from 'react';
import { useCarouselContext } from '../CarouselProvider/CarouselProvider';
import S from './MotionTrack.styles';
const MotionTrack = ({ children, enableCarousel = true, trackMutations = false, ...otherProps }) => {
    const animation = useAnimation();
    const carousel = useCarouselContext();
    const dragging = React.useRef(false);
    const xTransform = React.useRef(0);
    const maxOffset = carousel.offsets[carousel.maxIndex];
    const observer = !isSSR()
        ? new MutationObserver(() => {
            carousel.reinitialise(carousel.refTrack?.current?.childElementCount ?? 0, carousel.paddingOffset);
        })
        : null;
    React.useEffect(() => {
        if (!trackMutations || !observer || !carousel.refTrack?.current)
            return;
        observer.observe(carousel.refTrack?.current, { childList: true, subtree: true });
        return () => {
            if (observer) {
                observer.disconnect();
            }
        };
    }, [observer, trackMutations]);
    // Stop the drag momentum animation, and start a new animation to the correct offset.
    React.useEffect(() => {
        if (typeof carousel.offset !== 'undefined') {
            animation.stop();
            animation.start({ x: -carousel.offset });
        }
    }, [carousel.activeIndex]);
    return (React.createElement(S.Container, { ...otherProps, ref: carousel.refContainer },
        React.createElement(S.Track, { ref: carousel.refTrack, animate: animation, drag: enableCarousel ? 'x' : false, dragConstraints: { left: -maxOffset, right: 0 }, onClickCapture: (event) => {
                // Ignore click events after dragging, this prevents links being clicked when drag ends.
                if (dragging.current) {
                    event.preventDefault();
                    event.stopPropagation();
                    dragging.current = false;
                }
            }, onDragStart: () => {
                dragging.current = true;
            }, onDragEnd: (_, info) => {
                // Get the absolute x value, avoiding overflow when dragging left.
                const absX = Math.abs(Math.min(xTransform.current, 0));
                // Determine the next target index.
                let targetIndex = carousel.offsets.findIndex((offset) => offset > absX) ?? 0;
                // If it is -1 then we scrolled past the max offset value.
                if (targetIndex === -1) {
                    targetIndex = carousel.maxIndex;
                }
                // Ensure we take into account the drag direction.
                if (info.offset.x > 0) {
                    targetIndex = Math.max(0, targetIndex - 1);
                }
                // Update the carousel state.
                carousel.goToIndex(targetIndex);
                // Update to false, so that it doesn't take multiple clicks on a carouselCard to register.
                setTimeout(function () {
                    dragging.current = false;
                }, 10);
            }, onUpdate: (latest) => {
                // Store the current x position as the carousel is dragged.
                xTransform.current = typeof latest.x === 'number' ? latest.x : parseFloat(latest.x);
            } }, children)));
};
export default MotionTrack;
